import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ListOrdered, Plus, Play, Trash2, Pause, Loader2 } from 'lucide-react';

function StepRow({ step, onChange, onRemove, idx }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-end" data-testid={`seq-step-${idx}`}>
      <div className="col-span-7">
        <Label className="text-[10px] uppercase tracking-wide text-slate-400">Template name</Label>
        <Input
          data-testid={`seq-step-${idx}-template`}
          value={step.template_name}
          onChange={(e) => onChange({ ...step, template_name: e.target.value })}
          placeholder="hello_world"
        />
      </div>
      <div className="col-span-2">
        <Label className="text-[10px] uppercase tracking-wide text-slate-400">Lang</Label>
        <Input
          value={step.language}
          onChange={(e) => onChange({ ...step, language: e.target.value })}
          placeholder="en_US"
        />
      </div>
      <div className="col-span-2">
        <Label className="text-[10px] uppercase tracking-wide text-slate-400">Delay (h)</Label>
        <Input
          type="number"
          value={step.delay_hours}
          onChange={(e) => onChange({ ...step, delay_hours: parseInt(e.target.value || '0', 10) })}
        />
      </div>
      <div className="col-span-1 flex justify-end">
        <Button variant="ghost" size="icon" onClick={onRemove} className="text-slate-400 hover:text-rose-600">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function CreateSequenceDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([{ template_name: '', language: 'en_US', delay_hours: 0 }]);
  const [submitting, setSubmitting] = useState(false);

  const updateStep = (i, next) => setSteps((prev) => prev.map((s, idx) => (idx === i ? next : s)));
  const addStep = () =>
    setSteps((prev) => [...prev, { template_name: '', language: 'en_US', delay_hours: 24 }]);
  const removeStep = (i) => setSteps((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/sequences', { name, description, steps });
      toast.success('Sequence created');
      onCreated?.();
      setOpen(false);
      setName('');
      setDescription('');
      setSteps([{ template_name: '', language: 'en_US', delay_hours: 0 }]);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-seq-btn" className="bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2">
          <Plus className="w-4 h-4" /> New Sequence
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Create follow-up Sequence</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input data-testid="seq-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="3-step nudge" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this sequence does…" />
          </div>
          <div className="space-y-3">
            <Label>Steps</Label>
            {steps.map((s, i) => (
              <StepRow key={i} idx={i} step={s} onChange={(n) => updateStep(i, n)} onRemove={() => removeStep(i)} />
            ))}
            <Button data-testid="seq-add-step-btn" variant="outline" size="sm" onClick={addStep} className="gap-2">
              <Plus className="w-3 h-3" /> Add step
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid="seq-submit-btn"
            disabled={submitting || !name || steps.length === 0 || steps.some((s) => !s.template_name)}
            onClick={handleSubmit}
            className="bg-[#00D1B2] hover:bg-[#00B89C] text-white"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating…</> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnrollDialog({ sequenceId, onEnrolled }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/api/sequences/${sequenceId}/enroll`, { phone });
      toast.success('Contact enrolled');
      onEnrolled?.();
      setOpen(false);
      setPhone('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`seq-enroll-btn-${sequenceId}`} variant="outline" size="sm" className="gap-2">
          <Play className="w-3 h-3" /> Enroll
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Enroll contact</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Label>Phone (E.164)</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+919876543210" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !phone}>Enroll</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SequencesPage() {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/sequences');
      setSequences(res.sequences || []);
    } catch (e) {
      toast.error(`Could not load sequences: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/api/sequences/${s.id}`, { is_active: !s.is_active });
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sequence '${s.name}'?`)) return;
    try {
      await api.del(`/api/sequences/${s.id}`);
      await load();
      toast.success('Deleted');
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-8" data-testid="sequences-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Sequences</h2>
          <p className="text-sm text-slate-500 mt-1">
            Schedule multi-step WhatsApp follow-ups. Auto-cancel when the customer replies.
          </p>
        </div>
        <CreateSequenceDialog onCreated={load} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="sequences-list">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : sequences.length === 0 ? (
          <div className="p-16 text-center">
            <ListOrdered className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-700">No sequences yet</p>
            <p className="text-xs text-slate-400 mt-1">Build a multi-step nudge to re-engage qualified leads.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sequences.map((s) => (
              <div key={s.id} data-testid={`seq-row-${s.id}`} className="flex items-start justify-between gap-4 p-5 hover:bg-slate-50/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold text-slate-900 text-sm">{s.name}</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${s.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                      {s.is_active ? 'Active' : 'Paused'}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {s.steps?.length ?? 0} steps · trigger: {s.trigger}
                    </span>
                  </div>
                  {s.description && <p className="text-xs text-slate-500 mt-1">{s.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <EnrollDialog sequenceId={s.id} onEnrolled={load} />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleActive(s)}
                    data-testid={`seq-toggle-${s.id}`}
                  >
                    {s.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(s)}
                    data-testid={`seq-delete-${s.id}`}
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
